export function useMediaRecorder() {
    let stream: MediaStream | null = null;
    let recorder: MediaRecorder | null = null;
    let chunks: Blob[] = [];

    /**
     * Can this browser record in the page at all?
     *
     * A capability check and not a permission one: `navigator.permissions.query({ name: "camera" })`
     * is not implemented at this project's Safari floor, and `getUserMedia()` is where the
     * permission answer arrives anyway. MediaRecorder itself only reached Safari 14.1, and is
     * absent from the embedded browsers a review link opens in from Gmail and Outlook.
     */
    function isSupported(): boolean {
        return (
            typeof window.MediaRecorder !== "undefined" &&
            typeof navigator.mediaDevices?.getUserMedia === "function"
        );
    }

    function isOpen(): boolean {
        return stream !== null;
    }

    /**
     * Ask for the camera and the microphone, and keep the stream for the preview to show.
     *
     * `audio: true` is not decoration — a silent testimonial is not one.
     */
    async function open(): Promise<MediaStream> {
        stream = await navigator.mediaDevices.getUserMedia({
            video: { facingMode: "user" },
            audio: true,
        });

        return stream;
    }

    function start(): void {
        if (!stream) {
            throw new Error("The camera is not open.");
        }

        // Reset per take, not per composable: Retake calls start() again on the same instance, and
        // a shared array would splice the rejected take onto the front of the kept one — uploading
        // footage the customer discarded, in a file nothing could decode.
        chunks = [];

        // No mimeType: Cloudflare Stream transcodes every upload into HLS/DASH renditions, so
        // whichever container this browser prefers — WebM/VP8 in Chrome, MP4/H.264 in Safari — is
        // equally fine, while pinning one fails outright on the browser that does not support it.
        recorder = new MediaRecorder(stream);
        recorder.ondataavailable = (event: BlobEvent) => {
            if (event.data.size > 0) {
                chunks.push(event.data);
            }
        };
        // A chunk a second rather than one blob at the end: memory stays flat over a long clip, and
        // an unexpected stop still yields the footage recorded so far.
        recorder.start(1000);
    }

    /**
     * End the take and resolve with everything it recorded.
     */
    function stop(): Promise<Blob> {
        return new Promise((resolve, reject) => {
            const active = recorder;

            if (!active || active.state === "inactive") {
                reject(new Error("Nothing was being recorded."));

                return;
            }

            active.onstop = () =>
                resolve(new Blob(chunks, { type: active.mimeType }));
            active.onerror = () =>
                reject(new Error("The recording could not be completed."));
            active.stop();
        });
    }

    /**
     * Switch the camera and the microphone off.
     *
     * A camera light left on after the dialog closes is the single most alarming bug this feature
     * can ship, so every exit path runs this, and it is idempotent because several of them run in
     * sequence. The recorder is stopped before the tracks are: pulling the tracks out from under a
     * live recorder is what makes it raise instead of finishing.
     */
    function release(): void {
        if (recorder && recorder.state !== "inactive") {
            recorder.stop();
        }

        recorder = null;

        stream?.getTracks().forEach((track) => track.stop());
        stream = null;
    }

    return { isSupported, isOpen, open, start, stop, release };
}
